import React from 'react';

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from 'i18next-browser-languagedetector';

import { useTable, useSortBy, usePagination, useGlobalFilter, useAsyncDebounce, useRowSelect } from 'react-table';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import FormControl from 'react-bootstrap/FormControl';
import { Row, Col } from 'react-bootstrap';
import t from '../Translation/Translation';
import { isEmpty } from '../Util/Util';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import generateExcel from "zipcelx";

import './CustomTable.css';

// Define a default UI for filtering
function GlobalFilter({
    preGlobalFilteredRows,
    globalFilter,
    setGlobalFilter,
}) {
    const count = preGlobalFilteredRows.length
    const [value, setValue] = React.useState(globalFilter)
    
    const onChange = useAsyncDebounce(value => {
        console.log(value);
    setGlobalFilter(value || undefined)
    }, 200)

    return (
    <span>
        <FormControl
        name="globalFilter"
        value={value || ""}
        onChange={e => {
            setValue(e.target.value);
            onChange(e.target.value);
        }}
        onInput={e =>{
            setValue(e.target.value);
            onChange(e.target.value);
        }}
        placeholder={t('search')}
        style={{
            fontSize: '1.1rem',
            border: '0',
        }}
        />
    </span>
    )
}

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef()
        const resolvedRef = ref || defaultRef

        React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate
        }, [resolvedRef, indeterminate])

        return (
        <>
            <input type="checkbox" ref={resolvedRef} {...rest} />
        </>
        )
    }
)

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(LanguageDetector)
  .init({
    // the translations
    // (tip move them in a JSON file and import them,
    // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
    resources: {
        es: {
            translation: {
                search: "Buscar",
            }
        },
        en: {
            translation: {
                search: "Search",
            }
        }
    },
    fallbackLng: "es",

    interpolation: {
      escapeValue: false // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    }
  });

export default function CustomTable({keyField, columns, data, isFilter, isPagination, isSelection, hasActions, create, remove, hiddenColumns, desc, isExport, noFooter, pageLength}) { 
    // Use the state and functions returned from useTable to build your UI
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        footerGroups,
        rows,
        prepareRow,
        page, // Instead of using 'rows', we'll use page,
        // which has only the rows for the active page

        // The rest of these things are super handy, too ;)
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize, selectedRowIds },
        state,
        visibleColumns,
        preGlobalFilteredRows,
        setGlobalFilter,
        selectedFlatRows
    } = useTable({
        columns,
        data,
        initialState: {
            pageSize: (pageLength) ? pageLength : 10,
            sortBy: [
                {
                    id: keyField,
                    desc: desc
                }
            ],
            hiddenColumns: hiddenColumns ? hiddenColumns : []
        }
      },
      useGlobalFilter,
      useSortBy,
      usePagination,
      useRowSelect,
      hooks => {
        if(isSelection) {
            hooks.visibleColumns.push(columns => [
                // Let's make a column for selection
                {
                    id: 'selection',
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({ getToggleAllPageRowsSelectedProps }) => (
                        <div>
                        <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({ row }) => (
                        <div>
                        <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
            ])
        } else {
            hooks.visibleColumns.push(columns => [
                ...columns,
            ])
        }
      },
    )

    function getHeader(column) {
        if (column.totalVisibleHeaderCount === 1) {
          return [
            {
              value: column.Header,
              type: "string"
            }
          ];
        } else {
          const span = [...Array(column.totalVisibleHeaderCount - 1)].map(x => ({
            value: "",
            type: "string"
          }));
          return [
            {
              value: column.Header,
              type: "string"
            },
            ...span
          ];
        }
    }

    function getExcel() {
        const config = {
          filename: "table",
          sheet: {
            data: []
          }
        };
    
        const dataSet = config.sheet.data;
    
        // review with one level nested config
        // HEADERS
        headerGroups.forEach(headerGroup => {
          const headerRow = [];
          if (headerGroup.headers) {
            headerGroup.headers.forEach(column => {
                if(column && column.id !== 'actions')
                    headerRow.push(...getHeader(column));
            });
          }
    
          dataSet.push(headerRow);
        });
    
        /*console.log(page)
        gotoPage(0);
        for(let i = 0; i < pageCount; i++) {
            setTimeout(function(){
                console.log(page)
                if(page.length > 0) {
                    page.forEach(row => {
                        const dataRow = [];
                
                        Object.values(row.cells).forEach(cell => {
                            if(cell.column && cell.column.id !== 'actions') {
                                const value = cell.value;
                                dataRow.push({
                                    value,
                                    type: typeof value === "number" ? "number" : "string"
                                })
                            }
                        });
                
                        dataSet.push(dataRow);
                    });
                    if(canNextPage) {
                        nextPage();
                    }
                }
            }, 1000);
        }*/

        // FILTERED ROWS
        if (rows.length > 0) {
            //console.log(rows);
          rows.forEach(row => {
            const dataRow = [];
            Object.keys(row.values).forEach(key => {
                //console.log(row.values[key]);
                if(!key.startsWith("actions") && !hiddenColumns.includes(key)) {
                    const value = row.values[key];
                    dataRow.push({
                        value,
                        type: typeof value === "number" ? "number" : "string"
                    })
                }
            });
    
            dataSet.push(dataRow);
          });
        } else {
          dataSet.push([
            {
              value: "No data",
              type: "string"
            }
          ]);
        }
    
        return generateExcel(config);
      }
  
    // Render the UI for your table
    return (
        <>
            <div className="tableWrap">
                <Table striped bordered hover {...getTableProps()}>
                    <thead>
                    { isFilter && 
                        <tr>
                            {hasActions &&
                                <th>
                                    {create && <Button onClick={e=> create()}><FontAwesomeIcon icon={faPlus} /></Button>}
                                    {' ' /*TODO: Disabled no funciona*/}
                                    {remove && <Button variant="danger" onClick={e=> remove(selectedFlatRows)}><FontAwesomeIcon icon={faTrash} disabled={isEmpty(selectedRowIds) ? true : false}/></Button>}
                                </th>
                            }
                            <th colSpan={(hasActions && isExport) ? visibleColumns.length-2 : ((hasActions || isExport) ? visibleColumns.length-1 : visibleColumns.length)}
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                            <GlobalFilter
                                preGlobalFilteredRows={preGlobalFilteredRows}
                                globalFilter={state.globalFilter}
                                setGlobalFilter={setGlobalFilter}
                            />
                            </th>
                            {
                                isExport && 
                                <th>
                                    <Button variant="primary" onClick={() => getExcel()}>{t('Excel')}</Button>
                                </th>
                            }
                        </tr>
                    }
                    { !isFilter && hasActions &&
                        <tr>
                            <th colSpan={hasActions ? visibleColumns.length-1 : visibleColumns.length}
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                                <Button onClick={e=> create()}><FontAwesomeIcon icon={faPlus} /></Button>
                                {' ' /*TODO: Disabled no funciona*/}
                                {<Button variant="danger" onClick={e=> remove(selectedFlatRows)}><FontAwesomeIcon icon={faTrash} disabled={isEmpty(selectedRowIds) ? true : false}/></Button>}
                            </th>
                        </tr>
                    }
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                            <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                {column.render('Header')}
                                {/* Add a sort direction indicator */}
                                <span>
                                {column.isSorted
                                    ? column.isSortedDesc
                                    ? ' 🔽'
                                    : ' 🔼'
                                    : ''}
                                </span>
                            </th>
                        ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    { isPagination && page.map((row, i) => {
                        prepareRow(row)
                        return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map(cell => {
                            return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                            })}
                        </tr>
                        )
                    })}
                    { !isPagination && rows.map((row, i) => {
                        prepareRow(row)
                        return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map(cell => {
                            return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                            })}
                        </tr>
                        )
                    })}
                    </tbody>
                    {!noFooter && <tfoot>
                        {footerGroups.map(group => (
                        <tr {...group.getFooterGroupProps()}>
                            {group.headers.map(column => (
                            <td {...column.getFooterProps()}>{column.render('Footer')}</td>
                            ))}
                        </tr>
                        ))}
                    </tfoot>}
                </Table>
            </div>
            { isPagination && rows.length > 10 &&
                <Row className="text-center">
                    <Col>
                        <div>
                            <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}><FontAwesomeIcon icon={faAngleDoubleLeft}/></Button>
                            {' '}
                            <Button onClick={() => previousPage()} disabled={!canPreviousPage}><FontAwesomeIcon icon={faAngleLeft}/></Button>
                            {' '}
                            <span>
                            Page{' '}
                            <strong>
                                {pageIndex + 1} of {pageOptions.length}
                            </strong>{' '}
                            </span>
                            {' '}
                            <Button onClick={() => nextPage()} disabled={!canNextPage}><FontAwesomeIcon icon={faAngleRight}/></Button>
                            {' '}
                            <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}><FontAwesomeIcon icon={faAngleDoubleRight}/></Button>
                            {' '}

                            {   pageOptions.length > 1 &&
                                <span>
                                Go to page:{' '}
                                <input
                                    type="number"
                                    defaultValue={pageIndex + 1}
                                    min="1"
                                    max={pageOptions.length}
                                    onChange={e => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0
                                    gotoPage(page)
                                    }}
                                    style={{ width: '100px' }}
                                />
                                </span>
                            }
                            {' '}
                            <select
                            value={pageSize}
                            onChange={e => {
                                setPageSize(Number(e.target.value))
                            }}
                            >
                            {[10, 20, 30, 40, 50].map(pageSize => (
                                <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                                </option>
                            ))}
                            </select>
                        </div>
                    </Col>
                </Row>
            }
        </>
    )
}